import React, { useState, useEffect, useCallback, useRef } from 'react';
import { dersLabel } from '../lib/dersler';
import SatisEkleModal from './SatisEkleModal';
import SatisArsivModal from './SatisArsivModal';
import YayineviSecici from './YayineviSecici';
import YayineviListesi from './YayineviListesi';
import DenemeListesi from './DenemeListesi';
import DersMultiSelect from './ui/DersMultiSelect';
import {
  trTarih, trZaman, tlMetni, satisOzeti, noListesi,
  satildiMi, satisRozetMetni, satisRozetBaslik,
  satisaUygunluk, basimaUygun, soruNoAyristir, SORU_NO_TAVANI, secimYayineviCoz,
} from '../lib/satis';

// ============================================================================
// SATIŞ ARŞİVİ (2026-09-13 → 2026-09-14 münhasır satış). Hangi soru hangi
// yayınevine/firmaya ne zaman ne fiyata satıldı; iptal edilen satışlar SİLİNMEZ,
// arşivde "iptal edildi" izi (zaman, eden, neden) ile kalır (kullanıcı kararı).
//
// Üç sekme = üç sorgu (GET /api/satis):
//   Aktif satışlar            durum=aktif & tur=satis
//   Fiyat bekleyen teslimler  durum=aktif & tur=teslim  (PDF teslimi, fiyat yok)
//   İptal edilenler           durum=iptal & tur=tumu
// Sekme sayıları `sekme_sayilari` (durum/tur hariç diğer filtrelere uyar).
// Liste sayfalı (limit 50); CSV aynı filtrelerle `format=csv` (sayfasız).
//
// Ekleme YALNIZ bu ekrandan değil — ana soru listesinde toplu seçim çubuğundan
// da yapılır; ikisi aynı SatisEkleModal'ı, aynı POST /api/satis ucunu kullanır.
// (Basım ekranı modal açmaz: yayınevine teslim PDF indirme anında sunucuda
// yazılır.) Soru no'ya tıklamak SatisArsivModal'ı açar (geçmiş + satılan sürüm).
//
// NUMARAYLA SEÇ (2026-09-14, kullanıcı: "Satış için seçilecek sorular onaylanmış
// olmalıdır. Numarası yazılıp seçildiğinde pdf basım ve çözüm basım işlemleri
// yapılabilmelidir."): numaralar POST /api/sorular/batch {public_nolar} ile
// getirilir; her satırda onay durumu + satış bilgisi görünür.
//   * "Satışa Ekle" yalnız lib/satis `satisaUygunluk` = null olanları modala verir
//     (backend aynı koşulu 409 ile zorunlu tutar — hep-ya-hiç reddi önlenir).
//   * "Basım" yalnız ONAYLI olanların id'leriyle `onBasimaGonder` çağırır → App
//     Basım ekranını bu sorularla ve "Çözümler PDF’in sonunda" AÇIK açar. ÖSYM kitapçık
//     düzenini (210×290, 2 sütun, §16) basım motoru sağlar; burada seçim kurulur.
// Arşiv listesinde seçili satırlar da "🖨 Basım" ile aynı yoldan basıma gider.
//
// YAYINEVİ DB LİSTESİ (2026-09-15, kullanıcı kararı):
//   * Filtre "Yayınevi" = YayineviSecici (Tümü + pasifler dahil, ekleme yok);
//     sorguya `yayinevi_id` gider (eski `yayinevi` metin filtresi kalktı).
//   * 4. sekme "Yayınevleri" (YayineviListesi): ekle / yeniden adlandır / not /
//     pasife al. Sorgu sekmelerinin (SATIS_SEKMELERI) DIŞINDADIR — liste çekilmez.
//   * "Yeniden sorma": basıma / fiyatlandırmaya giderken seçili satırların
//     yayınevi `secimYayineviCoz` ile çözülür; karışık seçim basıma GİTMEZ.
//     `onBasimaGonder({ids, yayineviId, yayineviAd})`.
//   * `baslangicSekme`: Basım'daki "Satış Arşivi’nde gör" ('aktif' | 'teslim').
// ============================================================================
const BOS_FILTRE = { yayinevi_id: '', dersler: [], sinif: '', konu: '', baslangic: '', bitis: '', public_no: '' };
export const SATIS_LIMIT = 50;
export const YAYINEVI_SEKME = 'yayinevleri';
// Okul denemesi kayıtları (2026-10-09) — sorgu sekmesi DEĞİL (DenemeListesi kendi çeker).
export const DENEME_SEKME = 'denemeler';

export const SATIS_SEKMELERI = [
  { id: 'aktif', etiket: 'Aktif satışlar', durum: 'aktif', tur: 'satis', sayac: 'aktif_satis',
    aktifSinif: 'bg-emerald-600 text-white border-emerald-600', bos: 'Aktif satış kaydı yok' },
  { id: 'teslim', etiket: 'Fiyat bekleyen teslimler', durum: 'aktif', tur: 'teslim', sayac: 'teslim',
    aktifSinif: 'bg-amber-500 text-white border-amber-500', bos: 'Fiyat bekleyen teslim yok' },
  { id: 'iptal', etiket: 'İptal edilenler', durum: 'iptal', tur: 'tumu', sayac: 'iptal',
    aktifSinif: 'bg-gray-700 text-white border-gray-700', bos: 'İptal edilmiş kayıt yok' },
];

// Liste ve CSV AYNI parametre kümesini kurar; CSV sayfasızdır.
export function satisSorguParametreleri(sorgu, sekme, { sayfa = 1, limit = SATIS_LIMIT, csv = false } = {}) {
  const s = SATIS_SEKMELERI.find((x) => x.id === sekme) || SATIS_SEKMELERI[0];
  const p = new URLSearchParams();
  p.set('durum', s.durum);
  p.set('tur', s.tur);
  Object.entries(sorgu || {}).forEach(([k, v]) => {
    // Çoklu ders (2026-10-23): dizi → tek `ders` parametresi, virgülle (App.js ile aynı sözleşme).
    if (Array.isArray(v)) {
      const t = v.map((x) => String(x).trim()).filter(Boolean).join(',');
      if (t) p.set(k === 'dersler' ? 'ders' : k, t);
      return;
    }
    let t = String(v == null ? '' : v).trim();
    if (k === 'public_no') t = t.replace(/[^0-9]/g, '');
    if (t) p.set(k, t);
  });
  if (csv) p.set('format', 'csv');
  else { p.set('sayfa', String(sayfa)); p.set('limit', String(limit)); }
  return p;
}

// POST /api/sorular/batch {public_nolar} → { sorular, bulunamayan }. Backend
// istenen numara sırasını korur; her satır `satis` özetini taşır. Hata → throw.
// Sunucu tek istekte en çok SORU_NO_TAVANI (500) numara alır → uzun liste parça parça istenir (sıra korunur).
export async function numaraylaGetir(apiUrl, authFetch, nolar) {
  const sorular = [];
  const bulunamayan = [];
  for (let i = 0; i < nolar.length; i += SORU_NO_TAVANI) {
    const r = await authFetch(`${apiUrl}/api/sorular/batch`, {
      method: 'POST',
      body: JSON.stringify({ public_nolar: nolar.slice(i, i + SORU_NO_TAVANI) }),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok || !d || !Array.isArray(d.sorular)) {
      throw new Error((d && d.hata) || `Sorular alınamadı${r.status ? ` (HTTP ${r.status})` : ''}`);
    }
    sorular.push(...d.sorular);
    if (Array.isArray(d.bulunamayan)) bulunamayan.push(...d.bulunamayan.map(Number).filter(Number.isFinite));
  }
  return { sorular, bulunamayan };
}

// Basıma gidecek satırların yayınevi (tek olmalı). Karışıksa Türkçe hata metni,
// değilse { yayineviId, yayineviAd } — çağıran `onBasimaGonder`'a taşır.
export function basimYayineviCoz(satirlar) {
  const c = secimYayineviCoz(satirlar);
  if (c.karisik) {
    const adlar = c.yayinevleri.map((y) => y.ad || `#${y.id}`).join(', ');
    return { hata: `Seçili satırlar farklı yayınevlerine ait (${adlar}) — basıma gönderilmedi. Tek yayınevine ait satırları seçin.` };
  }
  return { yayineviId: c.yayineviId, yayineviAd: c.yayineviAd };
}

// Onay durumu rozeti. Bilinmeyen durum ham değeriyle gri basılır.
const DURUM_ROZET = {
  onaylandi: { metin: '✓ Onaylı', sinif: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  onay_bekliyor: { metin: 'Onay bekliyor', sinif: 'bg-amber-100 text-amber-800 border-amber-300' },
  reddedildi: { metin: 'Reddedildi', sinif: 'bg-red-100 text-red-800 border-red-300' },
};
export function DurumRozeti({ durum }) {
  const r = DURUM_ROZET[durum] || { metin: durum ? String(durum) : '—', sinif: 'bg-gray-100 text-gray-700 border-gray-300' };
  return (
    <span data-durum-rozet={durum || ''}
      className={`inline-block px-1.5 py-0.5 rounded border text-[11px] font-semibold whitespace-nowrap ${r.sinif}`}>
      {r.metin}
    </span>
  );
}

// "Numarayla seç" kartı. Satış modalı üst bileşende açılır (`onSatisaEkle`);
// satış kaydedilince üst bileşen `tazeleSayaci`'nı artırır → aynı numaralar
// yeniden getirilir, satış rozetleri güncellenir.
export function NumaraylaSecKarti({ apiUrl, authFetch, onSatisaEkle, onBasimaGonder, tazeleSayaci = 0 }) {
  const [girdi, setGirdi] = useState('');
  const [sonuc, setSonuc] = useState(null);        // { sorular, bulunamayan, gecersiz }
  const [secili, setSecili] = useState(new Set());  // public_no
  const [yukleniyor, setYukleniyor] = useState(false);
  const [hata, setHata] = useState('');
  const [basimHata, setBasimHata] = useState('');  // karışık yayınevi → basıma gidilmez
  const sonIstek = useRef(null);                    // { nolar, gecersiz } — tazeleme için
  const istekNo = useRef(0);
  const islenenSayac = useRef(tazeleSayaci);

  const getir = useCallback(async (nolar, gecersiz = []) => {
    const no = ++istekNo.current;
    setYukleniyor(true); setHata('');
    try {
      const d = await numaraylaGetir(apiUrl, authFetch, nolar);
      if (no !== istekNo.current) return;
      sonIstek.current = { nolar, gecersiz };
      setSonuc({ ...d, gecersiz });
      setSecili(new Set(d.sorular.map((s) => s.public_no)));   // varsayılan: tümü seçili
    } catch (e) {
      if (no !== istekNo.current) return;
      setSonuc(null);
      setHata(e.message || 'Sorular alınamadı');
    }
    setYukleniyor(false);
  }, [apiUrl, authFetch]);

  useEffect(() => {
    if (tazeleSayaci === islenenSayac.current) return;
    islenenSayac.current = tazeleSayaci;
    if (sonIstek.current) getir(sonIstek.current.nolar, sonIstek.current.gecersiz);
  }, [tazeleSayaci, getir]);

  const gonder = (e) => {
    e.preventDefault();
    const { nolar, gecersiz } = soruNoAyristir(girdi);
    if (nolar.length === 0) {
      setSonuc(null);
      setHata(gecersiz.length ? `Geçerli soru numarası yok: ${gecersiz.join(', ')}` : 'Soru numarası girin (ör. 12, 45, #88).');
      return;
    }
    if (nolar.length > SORU_NO_TAVANI) {
      setHata(`Tek seferde en çok ${SORU_NO_TAVANI} numara getirilebilir (${nolar.length} girildi).`);
      return;
    }
    getir(nolar, gecersiz);
  };

  const temizle = () => {
    istekNo.current += 1;
    sonIstek.current = null;
    setSonuc(null); setSecili(new Set()); setHata(''); setBasimHata(''); setYukleniyor(false);
  };

  const toggle = (no) => {
    setBasimHata('');
    setSecili((p) => {
      const n = new Set(p);
      if (n.has(no)) n.delete(no); else n.add(no);
      return n;
    });
  };

  const sorular = sonuc ? sonuc.sorular : [];
  const tumuSecili = sorular.length > 0 && sorular.every((s) => secili.has(s.public_no));
  const seciliSorular = sorular.filter((s) => secili.has(s.public_no));
  const satisUygun = seciliSorular.filter((s) => satisaUygunluk(s) === null);
  const satisDisi = seciliSorular.length - satisUygun.length;
  const basimUygun = seciliSorular.filter(basimaUygun);
  const basimDisi = seciliSorular.length - basimUygun.length;

  // Basım: onaylıların id'leri + (satılmışsa) TEK yayınevi ön-seçimi; karışık → gidilmez.
  const basimaGonder = () => {
    const c = basimYayineviCoz(basimUygun);
    if (c.hata) { setBasimHata(c.hata); return; }
    setBasimHata('');
    onBasimaGonder({ ids: basimUygun.map((s) => s.id).filter(Boolean), yayineviId: c.yayineviId, yayineviAd: c.yayineviAd });
  };

  return (
    <section data-numara-sec className="bg-white border border-gray-200 rounded-xl p-3 mb-4 shadow-sm">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 mb-2">
        <h3 className="text-sm font-bold text-gray-800">🔢 Numarayla seç</h3>
        <span className="text-[11px] text-gray-600">Satış için yalnız onaylı sorular seçilebilir.</span>
        <span className="text-[11px] text-gray-400">Basım: ÖSYM kitapçık düzeninde tek PDF — sorular + sonda çözümler.</span>
      </div>

      <form onSubmit={gonder} className="flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[220px]">
          <label htmlFor="satis-numara-girdi" className="block text-[10px] font-semibold text-gray-500 mb-0.5">
            Soru numaraları (virgül ya da boşlukla ayırın)
          </label>
          <input id="satis-numara-girdi" data-numara-girdi value={girdi} onChange={(e) => setGirdi(e.target.value)}
            placeholder="12, 45, #88" autoComplete="off"
            className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs font-mono" />
        </div>
        <button type="submit" disabled={yukleniyor}
          className="px-3 py-1.5 bg-gray-800 text-white text-xs font-semibold rounded hover:bg-gray-900 disabled:opacity-40">
          {yukleniyor ? 'Getiriliyor…' : 'Getir'}
        </button>
        {(sonuc || hata) && (
          <button type="button" onClick={temizle}
            className="px-3 py-1.5 border border-gray-300 text-xs rounded hover:bg-gray-50">Sonuçları temizle</button>
        )}
      </form>

      {hata && <div data-numara-hata className="mt-2 text-[11px] text-red-700 font-semibold">{hata}</div>}

      {sonuc && (
        <div className="mt-3 space-y-2">
          {sonuc.bulunamayan.length > 0 && (
            <div data-bulunamayan className="px-2.5 py-1.5 rounded-lg bg-amber-50 border border-amber-200 text-[11px] text-amber-900">
              Bulunamadı: {noListesi(sonuc.bulunamayan, 40)}
            </div>
          )}
          {sonuc.gecersiz.length > 0 && (
            <div data-gecersiz className="px-2.5 py-1.5 rounded-lg bg-amber-50 border border-amber-200 text-[11px] text-amber-900">
              Numara olmadığı için yok sayıldı: {sonuc.gecersiz.join(', ')}
            </div>
          )}

          {sorular.length > 0 && (
            <>
              <div className="overflow-x-auto border border-gray-100 rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-500 text-xs">
                    <tr>
                      <th className="px-3 py-2 w-8">
                        <input type="checkbox" aria-label="Getirilen soruların tümünü seç" checked={tumuSecili}
                          onChange={() => setSecili(tumuSecili ? new Set() : new Set(sorular.map((s) => s.public_no)))} />
                      </th>
                      <th className="text-left px-3 py-2 font-semibold">Soru No</th>
                      <th className="text-left px-3 py-2 font-semibold">Ders</th>
                      <th className="text-left px-3 py-2 font-semibold">Konu</th>
                      <th className="text-left px-3 py-2 font-semibold">Durum</th>
                      <th className="text-left px-3 py-2 font-semibold">Satış</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sorular.map((s) => {
                      const neden = satisaUygunluk(s);
                      return (
                        <tr key={s.id || s.public_no} data-numara-satir={s.public_no} className="border-t border-gray-100">
                          <td className="px-3 py-2">
                            <input type="checkbox" aria-label={`#${s.public_no} numara listesinde seç`}
                              checked={secili.has(s.public_no)} onChange={() => toggle(s.public_no)} />
                          </td>
                          <td className="px-3 py-2">
                            <span className="font-mono bg-gray-800 text-white px-1.5 py-0.5 rounded text-[11px]">#{s.public_no}</span>
                          </td>
                          <td className="px-3 py-2 text-gray-600">{dersLabel(s.ders)}</td>
                          <td className="px-3 py-2 text-gray-600 max-w-[180px] truncate" title={s.konu || ''}>{s.konu || '—'}</td>
                          <td className="px-3 py-2"><DurumRozeti durum={s.durum} /></td>
                          <td className="px-3 py-2">
                            <div className="flex flex-col items-start gap-0.5">
                              {satildiMi(s.satis) && (
                                <span title={satisRozetBaslik(s.satis)}
                                  className="px-1.5 py-0.5 rounded bg-emerald-50 border border-emerald-200 text-emerald-800 text-[11px] font-semibold whitespace-nowrap">
                                  {satisRozetMetni(s.satis)}
                                </span>
                              )}
                              {neden
                                ? <span data-neden className="text-[11px] text-red-600">{neden}</span>
                                : <span className="text-[11px] text-emerald-700">satışa uygun</span>}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex flex-wrap items-center gap-2 pt-1">
                <span className="text-[11px] text-gray-500">{seciliSorular.length}/{sorular.length} seçili</span>
                <button type="button" data-numara-satis disabled={satisUygun.length === 0}
                  onClick={() => onSatisaEkle(satisUygun.map((s) => s.public_no))}
                  title="Yalnız satışa uygun (onaylı, satılmamış, hatalı işaretlenmemiş) seçili sorular satış kaydına girer"
                  className="px-3 py-1.5 rounded bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed">
                  💰 Satışa Ekle ({satisUygun.length})
                </button>
                {satisDisi > 0 && (
                  <span data-satis-disi className="text-[11px] text-red-600">{satisDisi} soru satışa uygun değil, alınmayacak</span>
                )}
                {onBasimaGonder && (
                  <>
                    <button type="button" data-numara-basim disabled={basimUygun.length === 0}
                      onClick={basimaGonder}
                      title="Basım ekranı bu sorularla açılır: ÖSYM kitapçık düzeni, çözümler PDF’in sonunda; satılmış sorularda yayınevi ön-seçili gelir"
                      className="px-3 py-1.5 rounded bg-orange-600 text-white text-xs font-semibold hover:bg-orange-700 disabled:opacity-40 disabled:cursor-not-allowed">
                      🖨 Basım — soru + çözüm PDF ({basimUygun.length})
                    </button>
                    {basimDisi > 0 && (
                      <span data-basim-disi className="text-[11px] text-red-600">{basimDisi} seçili soru onaylı değil, basıma gitmez</span>
                    )}
                  </>
                )}
              </div>
              {basimHata && (
                <div data-basim-hata className="px-2.5 py-1.5 rounded-lg bg-red-50 border border-red-200 text-[12px] text-red-700 font-semibold">{basimHata}</div>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
}

// İptal nedeni ZORUNLU (en az 3 karakter) — boşken istek gitmez.
export function IptalNedenModal({ adet, onVazgec, onOnayla }) {
  const [neden, setNeden] = useState('');
  const [hata, setHata] = useState('');
  const [gonderiliyor, setGonderiliyor] = useState(false);
  const gecerli = neden.trim().length >= 3;
  const gonder = async (e) => {
    e.preventDefault();
    if (!gecerli) { setHata('İptal nedeni en az 3 karakter olmalı.'); return; }
    setGonderiliyor(true); setHata('');
    try {
      await onOnayla(neden.trim());
    } catch (err) {
      setHata(err.message || 'İptal edilemedi');
      setGonderiliyor(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onVazgec}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={gonder} data-iptal-modal
        className="bg-white rounded-xl shadow-xl w-full max-w-sm p-4 space-y-3">
        <div className="font-bold text-sm text-red-800">↩ {adet} satış kaydı iptal edilsin mi?</div>
        <div className="text-[11px] text-gray-600">
          Soru satıştan düşer ve <strong>soru havuzuna geri döner</strong> — yeniden basılabilir
          ve satılabilir. Kayıt silinmez; arşivde “iptal edildi” izi (iptal eden, zaman, neden) ile kalır.
          {' '}Bağlam temelli sorularda grubun diğer soruları da birlikte iptal edilir.
        </div>
        <div>
          <label className="block text-[11px] font-semibold text-gray-600 mb-1">İptal nedeni (zorunlu, en az 3 karakter)</label>
          <textarea value={neden} onChange={(e) => { setNeden(e.target.value); setHata(''); }} rows={2} maxLength={500} autoFocus
            placeholder="Örn. Yayınevi siparişi geri çekti"
            className="w-full px-2.5 py-1.5 rounded-lg border border-gray-300 text-sm resize-none" />
        </div>
        {hata && <div className="text-[11px] text-red-600 font-semibold">{hata}</div>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onVazgec}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-gray-300 hover:bg-gray-50">Vazgeç</button>
          <button type="submit" disabled={!gecerli || gonderiliyor}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-600 text-white hover:bg-red-700 disabled:opacity-40">
            {gonderiliyor ? 'İptal ediliyor…' : 'İptal et'}
          </button>
        </div>
      </form>
    </div>
  );
}

const bugunDamgasi = () => new Date().toISOString().slice(0, 10);

// Başlangıç sekmesi yalnız bilinen bir sekme olabilir; tanınmayan değer 'aktif'e düşer.
const sekmeDogrula = (s) => (s === YAYINEVI_SEKME || s === DENEME_SEKME || SATIS_SEKMELERI.some((x) => x.id === s) ? s : 'aktif');

export default function SoruSatisi({ apiUrl, authFetch, rolYeterli, onGeri, onBasimaGonder, baslangicSekme = 'aktif' }) {
  const [filtre, setFiltre] = useState(BOS_FILTRE);
  const [sorgu, setSorgu] = useState(BOS_FILTRE);
  const [sekme, setSekme] = useState(() => sekmeDogrula(baslangicSekme));
  const [sayfa, setSayfa] = useState(1);
  const [veri, setVeri] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [hata, setHata] = useState('');
  const [bilgi, setBilgi] = useState('');
  // SEÇİM SAYFALAR ARASINDA KORUNUR (2026-10-20, kullanıcı isteği "200 soru varsa 50 seçiyorum, sonraki sayfada
  // eklenmiyor"): public_no → o numaranın arşiv satırları. Yalnız sekme / filtre değişince ya da işlem (iptal,
  // satış) sonrası düşer; sayfa geçişi ve "Yenile" seçimi korur.
  const [secili, setSecili] = useState(() => new Map());
  const [tumuYukleniyor, setTumuYukleniyor] = useState(false);
  const [modal, setModal] = useState(null);          // SatisEkleModal: { nolar, mod, yayineviId, yayineviAd }
  const [iptalNolar, setIptalNolar] = useState(null); // IptalNedenModal açıksa public_no dizisi
  const [arsivNo, setArsivNo] = useState(null);       // SatisArsivModal açıksa public_no
  const [csvIndiriliyor, setCsvIndiriliyor] = useState(false);
  // Numarayla seç kartı: satış kaydedilince artar → kart aynı numaraları tazeler.
  const [numaraTazele, setNumaraTazele] = useState(0);
  // Arşiv satırlarından basım: public_no → id çözümü sürerken / sorunlu satır raporu.
  const [basimHazirlaniyor, setBasimHazirlaniyor] = useState(false);
  const [basimHata, setBasimHata] = useState('');
  // Hızlı sekme/sayfa geçişinde geç gelen eski yanıt yenisini ezmesin.
  const istekNo = useRef(0);

  const yukle = useCallback(async () => {
    // "Yayınevleri" sekmesi sorgu sekmesi değildir — liste çekilmez.
    if (sekme === YAYINEVI_SEKME || sekme === DENEME_SEKME) return;
    const no = ++istekNo.current;
    setYukleniyor(true); setHata('');
    try {
      const params = satisSorguParametreleri(sorgu, sekme, { sayfa, limit: SATIS_LIMIT });
      const r = await authFetch(`${apiUrl}/api/satis?${params.toString()}`);
      const d = await r.json();
      if (no !== istekNo.current) return;
      if (!d || !d.basarili) throw new Error((d && d.hata) || 'Liste alınamadı');
      setVeri(d);
      setBasimHata('');
    } catch (e) {
      if (no !== istekNo.current) return;
      setVeri(null);
      setHata(e.message || 'Hata');
    }
    setYukleniyor(false);
  }, [apiUrl, authFetch, sorgu, sekme, sayfa]);

  useEffect(() => { yukle(); }, [yukle]);

  const yayineviSekmesi = sekme === YAYINEVI_SEKME;
  const denemeSekmesi = sekme === DENEME_SEKME;
  const sekmeBilgi = SATIS_SEKMELERI.find((s) => s.id === sekme) || SATIS_SEKMELERI[0];
  const kayitlar = (veri && Array.isArray(veri.kayitlar)) ? veri.kayitlar : [];
  const toplamKayit = Number(veri?.toplam_kayit ?? veri?.toplam_adet ?? kayitlar.length) || 0;
  const limit = Number(veri?.limit) || SATIS_LIMIT;
  const toplamSayfa = Math.max(1, Math.ceil(toplamKayit / limit));
  // 2026-09-15: iptal satırları da seçilir (Basım / tekrar satış); "iptal et" o sekmede yok.
  const secimVar = true;
  const iptalSekmesi = sekme === 'iptal';

  // İptal / fiyatlandırma sonrası son sayfa boşalırsa geçerli son sayfaya dön.
  useEffect(() => {
    if (veri && sayfa > toplamSayfa) setSayfa(toplamSayfa);
  }, [veri, sayfa, toplamSayfa]);

  const filtreUygula = (e) => { e.preventDefault(); setBilgi(''); setSorgu({ ...filtre }); setSayfa(1); setSecili(new Map()); };
  const filtreTemizle = () => { setFiltre(BOS_FILTRE); setSorgu(BOS_FILTRE); setSayfa(1); setSecili(new Map()); };
  const sekmeSec = (id) => {
    if (id === sekme) return;
    setSekme(id); setSayfa(1); setSecili(new Map()); setBilgi(''); setBasimHata('');
  };

  const sayfaSatirlari = (no) => kayitlar.filter((k) => k.public_no === no);
  const toggle = (no) => setSecili((p) => {
    const n = new Map(p);
    if (n.has(no)) n.delete(no); else n.set(no, sayfaSatirlari(no));
    return n;
  });
  const tumuSecili = kayitlar.length > 0 && kayitlar.every((k) => secili.has(k.public_no));
  // Başlık kutusu YALNIZ bu sayfayı ekler/bırakır; diğer sayfaların seçimi kalır.
  const sayfayiSec = () => setSecili((p) => {
    const n = new Map(p);
    kayitlar.forEach((k) => { if (tumuSecili) n.delete(k.public_no); else n.set(k.public_no, sayfaSatirlari(k.public_no)); });
    return n;
  });
  const seciliKayitlar = [...secili.values()].flat();
  const seciliNolar = [...secili.keys()];

  // Filtredeki BÜTÜN kayıtlar (bütün sayfalar), LİSTE SIRASIYLA: sunucu sayfa başına en çok 500 satır verir.
  const tumKayitlariGetir = async () => {
    const n = new Map();
    for (let s = 1, toplam = Infinity; (s - 1) * 500 < toplam; s += 1) {
      const r = await authFetch(`${apiUrl}/api/satis?${satisSorguParametreleri(sorgu, sekme, { sayfa: s, limit: 500 }).toString()}`);
      const d = await r.json();
      if (!d || !d.basarili) throw new Error((d && d.hata) || 'Liste alınamadı');
      const satirlar = Array.isArray(d.kayitlar) ? d.kayitlar : [];
      satirlar.forEach((k) => n.set(k.public_no, [...(n.get(k.public_no) || []), k]));
      toplam = Number(d.toplam_kayit ?? d.toplam_adet ?? 0) || 0;
      if (satirlar.length === 0) break;
    }
    return n;
  };

  const tumunuSec = async () => {
    setTumuYukleniyor(true); setHata('');
    try {
      setSecili(await tumKayitlariGetir());
    } catch (e) {
      setHata(e.message || 'Liste alınamadı');
    }
    setTumuYukleniyor(false);
  };

  // SONDAN SAYIYLA SEÇ (2026-10-23, kullanıcı: "1000 soruyu aynı yayınevine yeniden basacağım; basım 500 ile
  // sınırlı. Elle sayı girilebilmeli, listedeki sorulardan sondan başa doğru seçilmeli"): filtredeki liste
  // (en yeni üstte) SONDAN başa çevrilir, ilk `atla` numara geçilir, sonraki `adet` numara seçilir. Seçim
  // sırası = basım sırası. 1000 soru → 1. parti adet 500 / atla 0, 2. parti adet 500 / atla 500.
  const [sondanAdet, setSondanAdet] = useState('');
  const [sondanAtla, setSondanAtla] = useState('');
  const sondanAdetSayi = Number(sondanAdet);
  const sondanAtlaSayi = sondanAtla === '' ? 0 : Number(sondanAtla);
  const sondanGecerli = Number.isInteger(sondanAdetSayi) && sondanAdetSayi >= 1 && sondanAdetSayi <= SORU_NO_TAVANI
    && Number.isInteger(sondanAtlaSayi) && sondanAtlaSayi >= 0;
  const sondanSec = async (e) => {
    e.preventDefault();
    if (!sondanGecerli) {
      setHata(`Adet 1-${SORU_NO_TAVANI} arasında, atla 0 ya da pozitif tam sayı olmalı.`);
      return;
    }
    setTumuYukleniyor(true); setHata(''); setBilgi('');
    try {
      const sirali = [...(await tumKayitlariGetir()).entries()].reverse();
      if (sondanAtlaSayi >= sirali.length) {
        setSecili(new Map());
        setHata(`Listede ${sirali.length} soru var; ${sondanAtlaSayi} atlanınca seçilecek soru kalmıyor.`);
      } else {
        const parca = sirali.slice(sondanAtlaSayi, sondanAtlaSayi + sondanAdetSayi);
        setSecili(new Map(parca));
        setBilgi(`Sondan ${sondanAtlaSayi + 1}-${sondanAtlaSayi + parca.length}. sorular seçildi (${parca.length} soru, toplam ${sirali.length}).`
          + (parca.length < sondanAdetSayi ? ` Listede yalnız ${parca.length} soru kaldı.` : ''));
      }
    } catch (err) {
      setHata(err.message || 'Liste alınamadı');
    }
    setTumuYukleniyor(false);
  };

  const iptalEt = async (neden) => {
    const nolar = iptalNolar || [];
    if (nolar.length === 0 || String(neden || '').trim().length < 3) return;
    const r = await authFetch(`${apiUrl}/api/satis/iptal`, {
      method: 'POST',
      body: JSON.stringify({ public_no: nolar, neden: String(neden).trim() }),
    });
    const d = await r.json().catch(() => ({}));
    if (!d || !d.basarili) throw new Error((d && d.hata) || 'İptal edilemedi');
    setIptalNolar(null);
    setSecili(new Map());
    // Bağlam grubu (2026-09-15, kullanıcı kararı "bağlam temelli sorular ayrı ayrı
    // satılamayacak"): bir üyenin iptali grubun aktif üyelerini de iptal eder —
    // sunucu birlikte iptal edilen kardeşleri `grup_eklenen` ile bildirir.
    const grupEklenen = Array.isArray(d.grup_eklenen) ? d.grup_eklenen : [];
    setBilgi(`${d.iptal_edilen ?? nolar.length} satış kaydı iptal edildi — sorular soru havuzuna geri döndü. `
      + '“İptal edilenler” sekmesinden yeniden basabilir ya da tekrar satabilirsiniz.'
      + (grupEklenen.length > 0 ? ` Bağlam grubu birlikte iptal edildi: ${noListesi(grupEklenen, 40)}.` : ''));
    yukle();
  };

  // Fiyatlandırma: seçili teslimler tek yayınevine aitse modal o yayınevinde başlar
  // ("yeniden sorma"); karışıksa seçici boş gelir, kullanıcı seçer.
  const fiyatlandirAc = () => {
    const c = secimYayineviCoz(seciliKayitlar);
    setModal({
      nolar: seciliNolar, mod: 'fiyatlandir',
      yayineviId: c.karisik ? null : c.yayineviId, yayineviAd: c.karisik ? '' : c.yayineviAd,
    });
  };

  // Arşiv satırları → Basım. Satış kaydı public_no taşır; basım soru id'si ister →
  // POST /api/sorular/batch {public_nolar} ile çözülür. Bulunamayan ya da artık
  // onaylı olmayan satır varsa basıma GİDİLMEZ (yayınevine eksik set basılmasın);
  // sorunlu numaralar listelenir, kullanıcı seçimi düzeltir. Seçili satırların
  // yayınevi Basım'a ön-seçili taşınır; farklı yayınevleri karışıksa gidilmez.
  const arsivBasimaGonder = async () => {
    if (!onBasimaGonder) return;
    const nolar = seciliNolar;
    if (nolar.length === 0) return;
    setBasimHata(''); setBilgi('');
    // Basım tek seferde en çok 500 soru alır (sunucu soruIds tavanı) — fazlası partilere bölünür.
    if (nolar.length > SORU_NO_TAVANI) {
      setBasimHata(`Basım tek seferde en çok ${SORU_NO_TAVANI} soru alır; ${nolar.length} seçili. `
        + `“Sondan seç” ile parti parti seçin (ör. adet ${SORU_NO_TAVANI} / atla 0, sonra adet ${SORU_NO_TAVANI} / atla ${SORU_NO_TAVANI}).`);
      return;
    }
    // İptal edilen satırlar: satış yok → yayınevi ön-seçilmez; havuz dışı satır basılmaz.
    if (iptalSekmesi) {
      const disari = havuzDisiMetni(seciliKayitlar);
      if (disari) {
        setBasimHata(`Havuz dışı: ${disari} — basıma gönderilmedi. Bu satırların seçimini kaldırıp yeniden deneyin.`);
        return;
      }
    }
    const yv = iptalSekmesi ? { yayineviId: null, yayineviAd: '', hata: '' } : basimYayineviCoz(seciliKayitlar);
    if (yv.hata) { setBasimHata(yv.hata); return; }
    setBasimHazirlaniyor(true);
    try {
      const d = await numaraylaGetir(apiUrl, authFetch, nolar);
      const onaysiz = d.sorular.filter((s) => !basimaUygun(s)).map((s) => s.public_no);
      const sorunlar = [];
      if (d.bulunamayan.length > 0) sorunlar.push(`Bulunamadı: ${noListesi(d.bulunamayan, 40)}`);
      if (onaysiz.length > 0) sorunlar.push(`Onaylı değil: ${noListesi(onaysiz, 40)}`);
      setBasimHazirlaniyor(false);
      if (sorunlar.length > 0) {
        setBasimHata(`${sorunlar.join(' · ')} — basıma gönderilmedi. Bu satırların seçimini kaldırıp yeniden deneyin.`);
        return;
      }
      onBasimaGonder({ ids: d.sorular.map((s) => s.id).filter(Boolean), yayineviId: yv.yayineviId, yayineviAd: yv.yayineviAd });
    } catch (e) {
      setBasimHazirlaniyor(false);
      setBasimHata(e.message || 'Sorular alınamadı');
    }
  };

  // Havuz dışı satırlar (arşiv "Havuz" sütunu, 2026-09-15): iptal edilen satışın sorusu
  // OTOMATİK havuza döner; yeniden satılmış / onaysız / 1 puanlı olan dönmez. Seçimde
  // böyle satır varsa işlem yapılmaz, satırlar nedenleriyle listelenir. Aynı soru arşivde
  // birden çok iptal satırında olabilir — numara başına bir kez yazılır.
  const havuzDisiMetni = (satirlar) => {
    const disarida = [...new Map(satirlar.filter((k) => k.havuzda === false).map((k) => [k.public_no, k])).values()];
    return disarida.map((k) => `#${k.public_no} (${k.havuz_notu || 'havuz dışı'})`).join(', ');
  };

  // İptal edilenler → TEKRAR SATIŞ: yalnız şu an havuzda olan sorular; yayınevi serbest
  // seçilir (eski alıcı ön-seçilmez — satış iptal edildi).
  const tekrarSatisaAc = () => {
    setBasimHata(''); setBilgi('');
    const disari = havuzDisiMetni(seciliKayitlar);
    if (disari) {
      setBasimHata(`Havuz dışı: ${disari} — satışa eklenmedi. Bu satırların seçimini kaldırıp yeniden deneyin.`);
      return;
    }
    setModal({ nolar: seciliNolar, mod: 'satis', yayineviId: null, yayineviAd: '' });
  };

  const csvIndir = async () => {
    setCsvIndiriliyor(true); setHata('');
    try {
      const params = satisSorguParametreleri(sorgu, sekme, { csv: true });
      const r = await authFetch(`${apiUrl}/api/satis?${params.toString()}`);
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.hata || `CSV alınamadı (HTTP ${r.status})`);
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `satis-arsivi-${sekme}-${bugunDamgasi()}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => { try { URL.revokeObjectURL(url); } catch (_) { /* yok say */ } }, 1000);
    } catch (e) {
      setHata(e.message || 'CSV indirilemedi');
    }
    setCsvIndiriliyor(false);
  };

  const ozetMetni = sekme === 'aktif' ? `${toplamKayit} aktif satış`
    : sekme === 'teslim' ? `${toplamKayit} teslim fiyat bekliyor`
    : `${toplamKayit} iptal kaydı`;

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center gap-3 mb-5">
        <button onClick={onGeri} className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg hover:bg-gray-50">← Geri</button>
        <h2 className="text-lg font-bold text-gray-800">💰 Satış Arşivi</h2>
      </div>

      {hata && <div className="mb-3 p-3 bg-red-50 border border-red-300 rounded-lg text-red-700 text-sm">{hata}</div>}
      {bilgi && <div className="mb-3 p-3 bg-green-50 border border-green-300 rounded-lg text-green-700 text-sm">{bilgi}</div>}

      {/* SEKMELER — üç sorgu sekmesi + "Yayınevleri" (sorgu dışı, DB listesi yönetimi) */}
      <div className="flex flex-wrap gap-1.5 mb-3" role="tablist" aria-label="Satış arşivi sekmeleri">
        {SATIS_SEKMELERI.map((s) => {
          const aktif = sekme === s.id;
          const sayi = veri?.sekme_sayilari?.[s.sayac];
          return (
            <button key={s.id} type="button" role="tab" aria-selected={aktif} data-sekme={s.id}
              onClick={() => sekmeSec(s.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${aktif ? s.aktifSinif : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>
              {s.etiket}
              {sayi != null && (
                <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] ${aktif ? 'bg-white/25' : 'bg-gray-100 text-gray-700'}`}>{sayi}</span>
              )}
            </button>
          );
        })}
        <button type="button" role="tab" aria-selected={yayineviSekmesi} data-sekme={YAYINEVI_SEKME}
          onClick={() => sekmeSec(YAYINEVI_SEKME)}
          title="Alıcı listesi (yayınevi, okul, kurum ya da kişi): ekle, yeniden adlandır, not, pasife al"
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${yayineviSekmesi
            ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>
          🏢 Alıcılar (kurum / kişi)
        </button>
        <button type="button" role="tab" aria-selected={denemeSekmesi} data-sekme={DENEME_SEKME}
          onClick={() => sekmeSec(DENEME_SEKME)}
          title="Okul kapaklı basılan denemeler: karekod sayfası, kitapçıklar, cevap anahtarını yayınla"
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${denemeSekmesi
            ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>
          📇 Denemeler
        </button>
      </div>

      {yayineviSekmesi ? (
        <YayineviListesi apiUrl={apiUrl} authFetch={authFetch} />
      ) : denemeSekmesi ? (
        <DenemeListesi apiUrl={apiUrl} authFetch={authFetch} />
      ) : (
      <>
      {/* FİLTRE */}
      <form onSubmit={filtreUygula} className="bg-white border border-gray-200 rounded-xl p-3 mb-4 shadow-sm flex flex-wrap items-end gap-2">
        {/* Yayınevi: DB listesinden (pasifler dahil), ekleme yok; sorguya yayinevi_id gider */}
        <YayineviSecici apiUrl={apiUrl} authFetch={authFetch}
          value={filtre.yayinevi_id === '' ? null : filtre.yayinevi_id}
          onChange={(y) => setFiltre({ ...filtre, yayinevi_id: y ? y.id : '' })}
          etiket="Yayınevi" bosSecenek="Tümü" allowInactive yeniEkle={false} />
        <div>
          <label className="block text-[10px] font-semibold text-gray-500 mb-0.5">Ders</label>
          <DersMultiSelect secili={Array.isArray(filtre.dersler) ? filtre.dersler : []}
            onChange={(d) => setFiltre({ ...filtre, dersler: Array.isArray(d) ? d : [] })} />
        </div>
        <div>
          <label className="block text-[10px] font-semibold text-gray-500 mb-0.5">Sınıf</label>
          <select value={filtre.sinif || ''} onChange={(e) => setFiltre({ ...filtre, sinif: e.target.value })}
            className="px-2 py-1.5 border border-gray-300 rounded text-xs bg-white w-28">
            <option value="">Tümü</option>
            <option value="9">9. sınıf</option>
            <option value="10">10. sınıf</option>
            <option value="11">11. sınıf</option>
            <option value="12">12. sınıf</option>
          </select>
        </div>
        <div>
          <label className="block text-[10px] font-semibold text-gray-500 mb-0.5">Konu</label>
          <input value={filtre.konu} onChange={(e) => setFiltre({ ...filtre, konu: e.target.value })}
            placeholder="Ara…" className="px-2 py-1.5 border border-gray-300 rounded text-xs w-32" />
        </div>
        <div>
          <label className="block text-[10px] font-semibold text-gray-500 mb-0.5">Soru No</label>
          <input value={filtre.public_no} onChange={(e) => setFiltre({ ...filtre, public_no: e.target.value })}
            placeholder="#123" className="px-2 py-1.5 border border-gray-300 rounded text-xs w-20" />
        </div>
        <div>
          <label className="block text-[10px] font-semibold text-gray-500 mb-0.5">Başlangıç</label>
          <input type="date" value={filtre.baslangic} onChange={(e) => setFiltre({ ...filtre, baslangic: e.target.value })}
            className="px-2 py-1.5 border border-gray-300 rounded text-xs" />
        </div>
        <div>
          <label className="block text-[10px] font-semibold text-gray-500 mb-0.5">Bitiş</label>
          <input type="date" value={filtre.bitis} onChange={(e) => setFiltre({ ...filtre, bitis: e.target.value })}
            className="px-2 py-1.5 border border-gray-300 rounded text-xs" />
        </div>
        <button type="submit" className="px-3 py-1.5 bg-blue-600 text-white text-xs font-semibold rounded hover:bg-blue-700">Filtrele</button>
        <button type="button" onClick={filtreTemizle} className="px-3 py-1.5 border border-gray-300 text-xs rounded hover:bg-gray-50">Temizle</button>
      </form>

      {/* NUMARAYLA SEÇ — satış (yalnız uygunlar) + basım (yalnız onaylılar) */}
      <NumaraylaSecKarti apiUrl={apiUrl} authFetch={authFetch} tazeleSayaci={numaraTazele}
        onSatisaEkle={(nolar) => { setBilgi(''); setModal({ nolar, mod: 'satis', yayineviId: null, yayineviAd: '' }); }}
        onBasimaGonder={onBasimaGonder} />

      {/* ÖZET */}
      {veri && (
        <div data-ozet className="bg-white border border-gray-200 rounded-xl p-3 mb-4 shadow-sm flex flex-wrap items-center gap-3 text-xs">
          <div className="font-bold text-gray-800">{ozetMetni}</div>
          {sekme === 'aktif' && <div className="font-bold text-emerald-700">{tlMetni(veri.toplam_tutar) || '0 ₺'}</div>}
          <div className="flex-1" />
          <div className="flex flex-wrap gap-1.5">
            {(veri.yayinevi_dagilimi || []).map((y) => (
              <span key={y.yayinevi || '—'} className="px-1.5 py-0.5 rounded bg-emerald-50 border border-emerald-200 text-emerald-800">
                {y.yayinevi || '—'} · {y.adet}{sekme === 'aktif' ? ` · ${tlMetni(y.tutar) || '0 ₺'}` : ''}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* LİSTE */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
        <div className="px-4 py-2.5 text-sm font-semibold text-gray-700 border-b border-gray-100 flex flex-wrap justify-between items-center gap-2">
          <span>{sekmeBilgi.etiket} ({kayitlar.length})</span>
          <div className="flex items-center gap-2 flex-wrap">
            {toplamKayit > 0 && (
              <span data-secim-ozet className="flex items-center gap-1.5 text-xs font-normal text-gray-600">
                {secili.size > 0 && <strong className="text-gray-800">{secili.size} seçili</strong>}
                {secili.size < toplamKayit && (
                  <button type="button" data-tumunu-sec onClick={tumunuSec} disabled={tumuYukleniyor}
                    title="Bu sekme ve filtredeki bütün kayıtları (bütün sayfalar) seçer"
                    className="px-2 py-0.5 rounded border border-blue-600 text-blue-700 hover:bg-blue-50 disabled:opacity-40">
                    {tumuYukleniyor ? 'Seçiliyor…' : `☑ Tüm sonuçları seç (${toplamKayit})`}
                  </button>
                )}
                {secili.size > 0 && (
                  <button type="button" data-secim-temizle onClick={() => setSecili(new Map())}
                    className="px-2 py-0.5 rounded border border-gray-300 hover:bg-gray-50">Seçimi temizle</button>
                )}
              </span>
            )}
            {secimVar && sekme === 'teslim' && secili.size > 0 && (
              <button onClick={fiyatlandirAc}
                className="px-2.5 py-1 rounded bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700">
                💰 Fiyatlandır ({secili.size})
              </button>
            )}
            {secimVar && secili.size > 0 && onBasimaGonder && (
              <button onClick={arsivBasimaGonder} disabled={basimHazirlaniyor} data-arsiv-basim
                title="Seçili satışların soruları Basım ekranında açılır: ÖSYM kitapçık düzeni, çözümler PDF’in sonunda"
                className="px-2.5 py-1 rounded bg-orange-600 text-white text-xs font-semibold hover:bg-orange-700 disabled:opacity-40">
                {basimHazirlaniyor ? 'Hazırlanıyor…' : `🖨 Basım (${secili.size})`}
              </button>
            )}
            {iptalSekmesi && secili.size > 0 && (
              <button onClick={tekrarSatisaAc} data-tekrar-satis
                title="Seçili iptal satırlarının soruları yeniden satışa eklenir (yalnız şu an havuzda olanlar)"
                className="px-2.5 py-1 rounded bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700">
                💰 Tekrar satışa ekle ({secili.size})
              </button>
            )}
            {secimVar && !iptalSekmesi && secili.size > 0 && (
              <button onClick={() => setIptalNolar(seciliNolar)}
                className="px-2.5 py-1 rounded bg-red-600 text-white text-xs font-semibold hover:bg-red-700">
                ↩ Seçilenleri iptal et ({secili.size})
              </button>
            )}
            <button onClick={csvIndir} disabled={csvIndiriliyor}
              title="Ekrandaki sekme ve filtrelerle tüm kayıtlar (sayfasız) CSV olarak iner"
              className="px-2.5 py-1 rounded border border-gray-300 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-40">
              {csvIndiriliyor ? 'Hazırlanıyor…' : '⬇ CSV indir'}
            </button>
            <button onClick={yukle} className="text-xs text-blue-600 hover:underline">Yenile</button>
          </div>
        </div>
        {toplamKayit > 0 && (
          <form data-sondan-sec onSubmit={sondanSec}
            className="px-4 py-2 border-b border-gray-100 bg-gray-50 flex flex-wrap items-center gap-2 text-xs text-gray-600">
            <span className="font-semibold text-gray-700">Sondan seç</span>
            <label className="flex items-center gap-1">Adet
              <input data-sondan-adet type="number" min={1} max={SORU_NO_TAVANI} value={sondanAdet}
                onChange={(e) => setSondanAdet(e.target.value)} placeholder={String(Math.min(SORU_NO_TAVANI, toplamKayit))}
                className="w-20 px-1.5 py-1 border border-gray-300 rounded text-xs" />
            </label>
            <label className="flex items-center gap-1"
              title="Sondan bu kadar soru geçilir. 2. parti için önceki partinin adedini yazın (ör. 500).">Atla
              <input data-sondan-atla type="number" min={0} value={sondanAtla}
                onChange={(e) => setSondanAtla(e.target.value)} placeholder="0"
                className="w-20 px-1.5 py-1 border border-gray-300 rounded text-xs" />
            </label>
            <button type="submit" disabled={tumuYukleniyor || !sondanGecerli}
              className="px-2.5 py-1 rounded border border-blue-600 text-blue-700 font-semibold hover:bg-blue-50 disabled:opacity-40">
              {tumuYukleniyor ? 'Seçiliyor…' : 'Seç'}
            </button>
            <span className="text-[11px] text-gray-500">
              Listenin en altından (en eski) başa doğru seçer; basım tek seferde en çok {SORU_NO_TAVANI} soru.
              {toplamKayit > SORU_NO_TAVANI && ` ${toplamKayit} soru için: 1. parti atla 0, 2. parti atla ${SORU_NO_TAVANI}${toplamKayit > 2 * SORU_NO_TAVANI ? '…' : ''}.`}
            </span>
          </form>
        )}
        {iptalSekmesi && (
          <div data-iptal-bilgi className="px-4 py-2 bg-emerald-50 border-b border-emerald-100 text-[12px] text-emerald-800">
            İptal edilen satışın sorusu <strong>otomatik olarak soru havuzuna döner</strong>. Satırları seçip yeniden
            basabilir (🖨 Basım) ya da tekrar satabilirsiniz (💰); “Havuz” sütunu sorunun şu anki durumunu gösterir.
          </div>
        )}
        {basimHata && (
          <div data-basim-hata className="px-4 py-2 bg-red-50 border-b border-red-200 text-[12px] text-red-700 font-semibold">{basimHata}</div>
        )}
        {yukleniyor ? (
          <div className="p-6 text-center text-gray-400 text-sm">Yükleniyor…</div>
        ) : kayitlar.length === 0 ? (
          <div className="p-6 text-center text-gray-400 text-sm">{sekmeBilgi.bos}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-xs">
                <tr>
                  {secimVar && (
                    <th className="px-3 py-2 w-8">
                      <input type="checkbox" aria-label="Sayfadakilerin tümünü seç" checked={tumuSecili}
                        onChange={sayfayiSec} />
                    </th>
                  )}
                  <th className="text-left px-3 py-2 font-semibold">Soru No</th>
                  <th className="text-left px-3 py-2 font-semibold">Ders</th>
                  <th className="text-left px-3 py-2 font-semibold">Konu</th>
                  <th className="text-left px-3 py-2 font-semibold">Yayınevi</th>
                  {sekme === 'teslim' ? (
                    <>
                      <th className="text-left px-3 py-2 font-semibold">Teslim</th>
                      <th className="text-left px-3 py-2 font-semibold">Deneme</th>
                      <th className="text-right px-3 py-2 font-semibold">Fiyat</th>
                    </>
                  ) : (
                    <>
                      <th className="text-left px-3 py-2 font-semibold">Satış tarihi</th>
                      <th className="text-right px-3 py-2 font-semibold">Fiyat</th>
                    </>
                  )}
                  {sekme === 'aktif' && <th className="text-left px-3 py-2 font-semibold">Not</th>}
                  {sekme === 'iptal' && (
                    <>
                      <th className="text-left px-3 py-2 font-semibold">İptal zamanı</th>
                      <th className="text-left px-3 py-2 font-semibold">İptal eden</th>
                      <th className="text-left px-3 py-2 font-semibold">İptal nedeni</th>
                      <th className="text-left px-3 py-2 font-semibold">Havuz</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {kayitlar.map((k, i) => (
                  <tr key={k.id ?? `${k.public_no}-${i}`} className="border-t border-gray-100">
                    {secimVar && (
                      <td className="px-3 py-2">
                        <input type="checkbox" aria-label={`#${k.public_no} seç`} checked={secili.has(k.public_no)} onChange={() => toggle(k.public_no)} />
                      </td>
                    )}
                    <td className="px-3 py-2">
                      <button type="button" onClick={() => setArsivNo(k.public_no)} data-arsiv-ac={k.public_no}
                        title="Satış geçmişi ve satılan sürüm"
                        className="font-mono bg-gray-800 text-white px-1.5 py-0.5 rounded text-[11px] hover:bg-blue-700">
                        #{k.public_no}
                      </button>
                    </td>
                    <td className="px-3 py-2 text-gray-600">{dersLabel(k.ders)}</td>
                    <td className="px-3 py-2 text-gray-600 max-w-[160px] truncate" title={k.konu || ''}>{k.konu || '—'}</td>
                    <td className="px-3 py-2 text-gray-800 font-medium">{k.yayinevi || '—'}</td>
                    {sekme === 'teslim' ? (
                      <>
                        <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{trZaman(k.isaretlenme_zamani) || trTarih(k.satis_tarihi) || '—'}</td>
                        <td className="px-3 py-2 text-gray-500 max-w-[160px] truncate" title={k.deneme_etiketi || ''}>{k.deneme_etiketi || '—'}</td>
                        <td className="px-3 py-2 text-right">
                          <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[11px] font-semibold">fiyat bekliyor</span>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{trTarih(k.satis_tarihi) || '—'}</td>
                        <td className="px-3 py-2 text-right font-semibold text-emerald-700 whitespace-nowrap">
                          {k.birim_fiyat != null ? tlMetni(k.birim_fiyat) : <span className="text-amber-700 font-normal text-[11px]">teslim · fiyat yok</span>}
                        </td>
                      </>
                    )}
                    {sekme === 'aktif' && (
                      <td className="px-3 py-2 text-gray-500 max-w-[200px] truncate" title={k.not_metni || ''}>{k.not_metni || '—'}</td>
                    )}
                    {sekme === 'iptal' && (
                      <>
                        <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{trZaman(k.iptal_zamani) || '—'}</td>
                        <td className="px-3 py-2 text-gray-500">{k.iptal_eden_email || '—'}</td>
                        <td className="px-3 py-2 text-gray-700 max-w-[220px] truncate" title={k.iptal_nedeni || ''}>{k.iptal_nedeni || '—'}</td>
                        <td className="px-3 py-2 whitespace-nowrap" data-havuz-durum={k.public_no}>
                          {k.havuzda === true ? (
                            <span title="Satış iptal edildi; soru otomatik olarak soru havuzuna döndü — basıma ve yeniden satışa açık"
                              className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 text-[11px] font-semibold">✓ Havuzda</span>
                          ) : k.havuzda === false ? (
                            <span title={k.havuz_notu || ''}
                              className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[11px]">Havuz dışı: {k.havuz_notu || '—'}</span>
                          ) : '—'}
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {/* SAYFALAMA */}
        <div data-sayfalama className="px-4 py-2 border-t border-gray-100 flex flex-wrap items-center justify-between gap-2 text-xs text-gray-600">
          <span>Toplam {toplamKayit} kayıt, sayfa {Math.min(sayfa, toplamSayfa)}/{toplamSayfa}</span>
          <div className="flex gap-1.5">
            <button type="button" disabled={sayfa <= 1 || yukleniyor} onClick={() => setSayfa((p) => Math.max(1, p - 1))}
              className="px-2.5 py-1 rounded border border-blue-600 text-blue-600 disabled:opacity-40 disabled:cursor-not-allowed">‹ Önceki</button>
            <button type="button" disabled={sayfa >= toplamSayfa || yukleniyor} onClick={() => setSayfa((p) => Math.min(toplamSayfa, p + 1))}
              className="px-2.5 py-1 rounded border border-blue-600 text-blue-600 disabled:opacity-40 disabled:cursor-not-allowed">Sonraki ›</button>
          </div>
        </div>
      </div>
      </>
      )}

      {modal && (
        <SatisEkleModal apiUrl={apiUrl} authFetch={authFetch} publicNolar={modal.nolar}
          mod={modal.mod} varsayilanYayineviId={modal.yayineviId} varsayilanYayineviAd={modal.yayineviAd}
          onKapat={() => setModal(null)}
          onTamam={(d) => { setModal(null); setBilgi(satisOzeti(d)); setNumaraTazele((n) => n + 1); setSecili(new Map()); yukle(); }} />
      )}
      {iptalNolar && (
        <IptalNedenModal adet={iptalNolar.length} onVazgec={() => setIptalNolar(null)} onOnayla={iptalEt} />
      )}
      {arsivNo != null && (
        <SatisArsivModal apiUrl={apiUrl} authFetch={authFetch} publicNo={arsivNo} onKapat={() => setArsivNo(null)} />
      )}
    </div>
  );
}
